import {useWatch} from "react-hook-form";
import * as Yup from "yup";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {EmailListField} from "@/components/form/fields/EmailListField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {IntegrationSelectField} from "@/components/form/fields/IntegrationSelectField/IntegrationSelectField.tsx";
import {PhoneListField} from "@/components/form/fields/PhoneListField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {WHATSAPP_CONTACT} from "@/lib/featureFlags.ts";
import {IntegrationProfile, PushNotificationProfile} from "@/pages/contacts/components/form/types.ts";

export interface ContactInfoFormValues {
  name: string;
  up_alerts_email_only: boolean;
  email_list: string[];
  sms_list: string[];
  phonecall_list: string[];
  whatsapp_list: string[];
  integrations: number[];
  push_notification_profiles: number[];
}

export type FormValuesKey = keyof ContactInfoFormValues;

export const EditContactInfoSchema = Yup.object({
  id: Yup.number().optional(),
  name: Yup.string().required("This field cannot be blank.").max(100),
  email_list: Yup.array().of(Yup.string().defined().email()).defined(),
  sms_list: Yup.array().of(Yup.string().defined()).defined(),
  phonecall_list: Yup.array().of(Yup.string().defined()).defined(),
  whatsapp_list: Yup.array().of(Yup.string().defined()).defined(),
  integrations: Yup.array().of(Yup.number().defined()).defined(),
  push_notification_profiles: Yup.array().of(Yup.number().defined()).defined(),
  up_alerts_email_only: Yup.boolean().required(),
});

interface EditContactInfoFormProps {
  integrationProfiles: IntegrationProfile[];
  pushNotificationProfiles: PushNotificationProfile[];
}

export const EditContactInfoForm = ({integrationProfiles, pushNotificationProfiles}: EditContactInfoFormProps) => {
  const {userHasFeature} = useCurrentUserContext();
  const showPushNotifications = pushNotificationProfiles?.length > 0;
  const selectedPushNotificationProfiles: number[] = useWatch({name: "push_notification_profiles"}) ?? [];
  const hasDisabledPushNotifications = pushNotificationProfiles?.some(
    ({id, is_errored}) => is_errored && selectedPushNotificationProfiles.includes(id)
  );
  const pushNotificationOptions = (pushNotificationProfiles ?? []).map(({id, display_name, is_errored}) => ({
    label: is_errored ? `${display_name} (disabled)` : display_name,
    value: id,
  }));
  const disabledPushNotificationsHint = hasDisabledPushNotifications
    ? "Devices marked (disabled) stopped accepting push notifications. Change any notification setting in the Uptime.com app on that device to re-enable it."
    : undefined;
  const showWhatsApp = userHasFeature(WHATSAPP_CONTACT);
  return (
    <>
      <div className="row mb-3">
        <div className="col">
          <InputField name="name" label="Name" required />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col">
          <EmailListField name="email_list" label="Emails" />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col">
          <IntegrationSelectField name="integrations" label="Integrations" integrationProfiles={integrationProfiles} />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-lg-6 mb-3 mb-lg-0">
          <PhoneListField name="sms_list" label="SMS" />
        </div>
        <div className="col-lg-6">
          <PhoneListField name="phonecall_list" label="Phone Call" />
        </div>
      </div>
      {(showPushNotifications || showWhatsApp) && (
        <div className="row mb-3">
          {showPushNotifications && (
            <div className="col-lg-6 mb-3 mb-lg-0">
              <SelectField
                name="push_notification_profiles"
                label="Push Notifications"
                placeholder="Select Push Notifications..."
                options={pushNotificationOptions}
                helpText={disabledPushNotificationsHint}
                isMulti
              />
            </div>
          )}
          {showWhatsApp && (
            <div className="col-lg-6">
              <PhoneListField name="whatsapp_list" label="WhatsApp" />
            </div>
          )}
        </div>
      )}
      <SwitchField name="up_alerts_email_only" label="Send UP alerts by email and integrations only" />
    </>
  );
};
